import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import { ExternalLinkIcon } from '@/components/icons/external-link';
import { PlaneSendIcon } from '@/components/icons/plane-send';
import { Button } from '@/components/ui/button';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import {
  BOOST,
  INDEED,
  JOVEO,
  POOL,
  REACH,
  SPRING,
  TALROO,
  ZIPRECRUITER,
} from '@/types/channels';

const REACH_SUPPORT_URL =
  'https://support.fountain.com/en/articles/9899478-fountain-reach';

interface CardButtonProps {
  channel: string;
  openingId?: string;
}

export function CardButton({ channel, openingId }: CardButtonProps) {
  const navigate = useNavigate();
  const { postJobPath } = useNavigationPaths();
  const { setSelectedChannelSlugs, setSelectedOpeningIds } =
    useJobPostingContext();

  const handlePostJobClick = useCallback(() => {
    if (!openingId) return;

    setSelectedOpeningIds([openingId]);
    setSelectedChannelSlugs([channel]);

    navigate(postJobPath);
  }, [
    channel,
    navigate,
    openingId,
    postJobPath,
    setSelectedChannelSlugs,
    setSelectedOpeningIds,
  ]);

  switch (channel) {
    case INDEED:
    case BOOST:
    case SPRING:
    case JOVEO:
    case TALROO:
    case POOL:
    case ZIPRECRUITER:
      return (
        <Button variant="secondary" onClick={handlePostJobClick}>
          <PlaneSendIcon />
          <FormattedMessage defaultMessage="Post Job" />
        </Button>
      );
    case REACH:
      return (
        <Button variant="secondary" asChild>
          <Link to={REACH_SUPPORT_URL} target="_blank">
            <ExternalLinkIcon className="text-fx-primary" />
            <FormattedMessage defaultMessage="Learn More" />
          </Link>
        </Button>
      );
    default:
      return null;
  }
}
